import type {
  CraftRequest,
  CraftResponseValid,
  CraftingGrid,
  GeneratedItem,
  ItemCategory,
  ItemId,
  ItemMeta,
  RecipeType,
  SlotIndex,
  TextureCatalogEntry,
  TextureId,
} from '@/types/domain';
import { normalize } from './RecipeNormalizer';
import { textureCatalog } from './TextureCatalog';
import { textureCandidateRetriever } from './TextureCandidateRetriever';
import { validateProgression } from './ProgressionValidator';
import { sanitizeText, sanitizeTags } from '@/utils/sanitize';
import { customItemId } from '@/utils/id';

export interface BuildRequestOptions {
  ingredients: ItemMeta[];
  recentDiscoveries: GeneratedItem[];
  recipeType: RecipeType;
  progressionStrictness: number;
  model?: string;
  lockTextureId?: TextureId;
  lockCategory?: ItemCategory;
}

function resolveTexture(textureId: TextureId, candidates: TextureCatalogEntry[], tags: string[]): TextureId {
  if (textureCatalog.has(textureId)) return textureId;
  // Fallback: best candidate by tag overlap, else first candidate, else a generic.
  let best: TextureCatalogEntry | undefined;
  let bestScore = -1;
  for (const c of candidates) {
    const score = c.tags.filter((t) => tags.includes(t)).length;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  if (best) return best.id;
  if (candidates.length) return candidates[0].id;
  return 'minecraft:stone';
}

export class CustomRecipeEngine {
  buildRequest(grid: CraftingGrid, opts: BuildRequestOptions): CraftRequest {
    const candidates = textureCandidateRetriever.retrieve(opts.ingredients, {
      lockTextureId: opts.lockTextureId,
      lockCategory: opts.lockCategory,
    });
    return {
      grid,
      ingredients: opts.ingredients,
      textureCandidates: candidates,
      recipeType: opts.recipeType,
      recentDiscoveries: opts.recentDiscoveries.map((d) => ({
        name: d.name,
        category: d.category,
        tags: d.tags,
        tier: d.tier,
      })),
      progressionStrictness: opts.progressionStrictness,
      model: opts.model,
      lockTextureId: opts.lockTextureId,
      lockCategory: opts.lockCategory,
    };
  }

  /** Turn a validated "valid" AI response into a storable GeneratedItem. */
  materialize(
    grid: CraftingGrid,
    response: CraftResponseValid,
    ingredients: ItemMeta[],
    candidates: TextureCatalogEntry[],
    parentRecipeKeys: Map<ItemId, string>,
    strictness: number,
  ): GeneratedItem {
    const norm = normalize(grid, response.recipeType);
    const ingredientIds = norm.ingredientIds;

    const progression = validateProgression(response, ingredients, strictness);

    const tags = sanitizeTags(response.tags);
    const inheritedTraits = sanitizeTags(response.inheritedTraits);
    const newTraits = sanitizeTags(response.newTraits);
    const functionalTraits = sanitizeTags(response.functionalTraits);

    const resolvedTextureId = resolveTexture(response.textureId, candidates, tags);
    const textureReused = true; // custom items always reuse an existing texture

    const ingredientPositions: (SlotIndex | null)[] = ingredientIds.map((id, idx) => {
      if (response.recipeType === 'shapeless') return null;
      // find the slot containing this occurrence (occurrence-aware)
      let seen = 0;
      for (let s = 0; s < 9; s++) {
        const cell = grid[s];
        if (cell && cell.itemId === id) {
          if (seen === idx) return s as SlotIndex;
          seen++;
        }
      }
      return null;
    });

    const parentRecipeIds = ingredientIds
      .filter((id) => id.startsWith('custom:'))
      .map((id) => parentRecipeKeys.get(id))
      .filter((v): v is string => typeof v === 'string');

    return {
      id: customItemId(),
      name: sanitizeText(response.name, 40),
      description: sanitizeText(response.description, 220),
      textureId: resolvedTextureId,
      textureReused,
      ingredientIds,
      ingredientPositions,
      recipeType: response.recipeType,
      discoveredAt: Date.now(),
      category: response.category,
      tags,
      inheritedTraits,
      newTraits,
      traits: [...inheritedTraits, ...newTraits],
      rarity: progression.rarity,
      tier: progression.tier,
      isUpgrade: !!response.isUpgrade,
      isStructure: !!response.isStructure,
      isMachine: !!response.isMachine,
      functionalTraits,
      parentRecipeIds,
      isVanilla: false,
      model: 'openrouter',
      reasoning: progression.clamped ? progression.warnings.join(' ') : undefined,
      connectionExplanation: sanitizeText(response.connectionExplanation, 220),
      textureExplanation: sanitizeText(response.textureExplanation, 220),
    };
  }
}

export const customRecipeEngine = new CustomRecipeEngine();
